import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  CalendarDays, FileDown, TrendingUp, TrendingDown, Minus, Trophy, AlertTriangle,
  Loader2, MoreVertical, Trash2, Plus, Sliders, Sparkles, Calendar as CalendarIcon,
  Activity,
} from "lucide-react";

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

// Etichette leggibili per le categorie tecniche
const CAT_LABELS = {
  camere: "Camere",
  bagni_comuni: "Bagni Comuni",
  reception: "Reception",
  hall: "Hall",
  colazione: "Colazione",
  aree_esterne: "Aree Esterne",
  ascensori: "Ascensori",
  corridoi: "Corridoi",
  personale: "Presentazione Staff",
  locali_tecnici: "Locali Tecnici",
  back_office: "Back Office",
  standard_generale: "Standard Generale",
  housekeeping: "Housekeeping",
  front_office: "Front Office",
};
const labelForCat = (c) => CAT_LABELS[c] || c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

function fmtIsoWeek(week_iso, date_from, date_to) {
  try {
    const [y, w] = week_iso.split("-W");
    const d0 = new Date(date_from);
    const d1 = new Date(date_to);
    return `Sett ${w} · ${d0.getDate()} ${MESI[d0.getMonth()]} – ${d1.getDate()} ${MESI[d1.getMonth()]} ${y}`;
  } catch {
    return week_iso;
  }
}
function fmtRange(date_from, date_to) {
  try {
    const d0 = new Date(date_from);
    const d1 = new Date(date_to);
    const y0 = d0.getFullYear();
    const y1 = d1.getFullYear();
    if (y0 === y1) {
      return `${d0.getDate()} ${MESI[d0.getMonth()]} – ${d1.getDate()} ${MESI[d1.getMonth()]} ${y1}`;
    }
    return `${d0.getDate()} ${MESI[d0.getMonth()]} ${y0} – ${d1.getDate()} ${MESI[d1.getMonth()]} ${y1}`;
  } catch { return `${date_from} – ${date_to}`; }
}

function DeltaBadge({ pct }) {
  if (pct == null) return <span className="text-xs text-muted-foreground">—</span>;
  const color = pct > 0
    ? "text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30"
    : pct < 0
      ? "text-rose-600 bg-rose-100 dark:bg-rose-900/30"
      : "text-muted-foreground bg-muted";
  const Icon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`} data-testid="delta-badge">
      <Icon className="h-3 w-3" />
      {pct > 0 ? "+" : ""}{pct}%
    </span>
  );
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDaysIso = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function ReportSettimanali() {
  const { user } = useAuth();
  const isSA = user?.role === "super_admin";
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterTipo, setFilterTipo] = useState("all"); // all | controllo_camere | custom

  // Custom builder modal state
  const [customOpen, setCustomOpen] = useState(false);
  const [customReportType, setCustomReportType] = useState("quality"); // "quality" | "productivity"
  const [customTitle, setCustomTitle] = useState("");
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [rangePreset, setRangePreset] = useState("last_7"); // last_7 | last_30 | last_month | custom
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [availableHotels, setAvailableHotels] = useState([]);
  const [availableCats, setAvailableCats] = useState([]);
  const [creating, setCreating] = useState(false);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/weekly-reports?tipo=all&limit=100`);
      setReports(data || []);
    } catch (e) {
      toast.error("Impossibile caricare i report");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Fetch dati per modal (hotel + categorie) al primo apri
  const openCustom = async () => {
    // Preset default: ultimi 7 giorni
    const t = todayIso();
    setRangeFrom(addDaysIso(t, -6));
    setRangeTo(t);
    setRangePreset("last_7");
    setCustomTitle(`Report Personalizzato · ${new Date().toLocaleDateString("it-IT")}`);
    setCustomReportType("quality");
    setSelectedHotels([]);
    setSelectedCats([]);
    setCustomOpen(true);
    try {
      const [hRes, cRes] = await Promise.all([
        api.get("/hotels"),
        api.get("/weekly-reports/categories-distinct"),
      ]);
      setAvailableHotels(hRes.data || []);
      setAvailableCats(cRes.data?.categories || []);
    } catch {
      toast.error("Errore caricamento dati modal");
    }
  };

  const applyPreset = (p) => {
    setRangePreset(p);
    const t = todayIso();
    if (p === "last_7") { setRangeFrom(addDaysIso(t, -6)); setRangeTo(t); }
    else if (p === "last_30") { setRangeFrom(addDaysIso(t, -29)); setRangeTo(t); }
    else if (p === "last_month") {
      const d = new Date();
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth(), 0);
      setRangeFrom(first.toISOString().slice(0, 10));
      setRangeTo(last.toISOString().slice(0, 10));
    }
    // custom: mantieni valori correnti
  };

  const toggleHotel = (id) => setSelectedHotels((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleCat = (c) => setSelectedCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  const toggleAllHotels = () => setSelectedHotels(selectedHotels.length === availableHotels.length ? [] : availableHotels.map((h) => h.id));
  const toggleAllCats = () => setSelectedCats(selectedCats.length === availableCats.length ? [] : [...availableCats]);

  const rangeDays = useMemo(() => {
    if (!rangeFrom || !rangeTo) return 0;
    const d0 = new Date(rangeFrom); const d1 = new Date(rangeTo);
    return Math.round((d1 - d0) / 86400000) + 1;
  }, [rangeFrom, rangeTo]);

  const canGenerate = selectedHotels.length > 0 && rangeFrom && rangeTo && rangeDays > 0 && rangeDays <= 90;

  const submitCustom = async () => {
    if (!canGenerate) return;
    setCreating(true);
    try {
      const payload = {
        report_type: customReportType,
        hotel_ids: selectedHotels,
        task_categories: customReportType === "productivity" ? [] : selectedCats,
        date_from: rangeFrom,
        date_to: rangeTo,
        tipo_label: customTitle.trim() || undefined,
      };
      const { data } = await api.post("/weekly-reports/generate-custom", payload);
      toast.success(`Report "${data.label}" generato — ${data.hotels?.length ?? 0} hotel`);
      setCustomOpen(false);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore generazione report personalizzato";
      toast.error(msg);
    } finally { setCreating(false); }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/weekly-reports/generate?overwrite=true");
      toast.success(`Report ${data.week_iso} generato — ${data.hotels} hotel · media ${data.corporate_avg ?? "—"}/100`);
      await load();
    } catch (e) {
      toast.error("Errore generazione");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async (report) => {
    try {
      const res = await api.get(`/weekly-reports/${report.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const filename = report.tipo === "custom"
        ? `report-custom-${(report.label || report.date_from).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`
        : `report-settimanale-${report.week_iso}.pdf`;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF scaricato");
    } catch { toast.error("Errore download PDF"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/weekly-reports/${deleteTarget.id}`);
      toast.success("Report eliminato");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Impossibile eliminare");
    } finally { setDeleting(false); }
  };

  const filteredReports = useMemo(() => {
    if (filterTipo === "all") return reports;
    if (filterTipo === "custom_quality") {
      return reports.filter((r) => r.tipo === "custom" && r.report_type !== "productivity");
    }
    if (filterTipo === "custom_productivity") {
      return reports.filter((r) => r.tipo === "custom" && r.report_type === "productivity");
    }
    return reports.filter((r) => (r.tipo || "controllo_camere") === filterTipo);
  }, [reports, filterTipo]);

  return (
    <div className="space-y-6 ih-anim-in" data-testid="report-settimanali-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Performance</div>
          <h1 className="font-display text-3xl tracking-tight font-light">Report Settimanali</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Riepilogo controllo camere generato ogni lunedì · più report personalizzati on-demand.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isSA && (
            <button
              data-testid="open-custom-builder"
              onClick={openCustom}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-lavender-500 hover:bg-lavender-600 text-white shadow-md"
            >
              <Plus className="h-4 w-4" />
              Nuovo report personalizzato
            </button>
          )}
          {isSA && (
            <button
              data-testid="generate-weekly-now"
              onClick={generateNow}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border border-lavender-400/70 text-lavender-800 dark:text-lavender-200 hover:bg-lavender-100/50 dark:hover:bg-lavender-900/40 disabled:opacity-60"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
              Genera settimana corrente
            </button>
          )}
        </div>
      </div>

      {/* Filtro tipo */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Mostra:</span>
        <div className="inline-flex rounded-full border border-border overflow-hidden text-sm" data-testid="filter-tipo">
          {[
            { k: "all", l: "Tutti" },
            { k: "controllo_camere", l: "Settimanali" },
            { k: "custom_quality", l: "Personalizzati" },
            { k: "custom_productivity", l: "Solo produttività" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilterTipo(f.k)}
              data-testid={`filter-tipo-${f.k}`}
              className={`px-3 py-1.5 transition-colors ${filterTipo === f.k
                ? "bg-lavender-500 text-white"
                : "bg-background hover:bg-accent text-foreground"
              }`}
            >{f.l}</button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">{filteredReports.length} report</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="ih-glass rounded-2xl p-10 text-center text-muted-foreground" data-testid="reports-empty">
          Nessun report {filterTipo === "custom" ? "personalizzato" : filterTipo === "controllo_camere" ? "settimanale" : ""}.
          {isSA && filterTipo !== "custom" && " Usa \"Genera settimana corrente\" per crearne uno."}
          {isSA && filterTipo === "custom" && " Usa \"Nuovo report personalizzato\" per crearne uno."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredReports.map((r) => {
            const cs = r.corporate_summary || {};
            const isCustom = r.tipo === "custom";
            const isProductivity = isCustom && r.report_type === "productivity";
            const kpiValue = isProductivity ? cs.avg_produttivita : cs.avg_punteggio;
            const kpiLabel = isProductivity ? "cam/h media" : "/100 corporate";
            const cardTestId = isProductivity
              ? `productivity-card-${r.id}`
              : isCustom ? `custom-card-${r.id}` : `weekly-card-${r.week_iso}`;
            const borderClass = isProductivity
              ? "border-l-sky-500"
              : isCustom ? "border-l-slate-400 dark:border-l-slate-500" : "border-l-lavender-400";
            return (
              <div
                key={r.id}
                data-testid={cardTestId}
                className={`ih-glass rounded-2xl p-5 ih-card-hover relative border-l-4 ${borderClass}`}
              >
                {/* Badge tipo + menu */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      {isProductivity ? (
                        <>
                          <span data-testid="badge-productivity" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200">
                            <Activity className="h-3 w-3" />
                            Produttività
                          </span>
                          <span data-testid="badge-custom" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200">
                            <Sliders className="h-3 w-3" />
                            Personalizzato
                          </span>
                        </>
                      ) : isCustom ? (
                        <span data-testid="badge-custom" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-slate-200/70 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200">
                          <Sliders className="h-3 w-3" />
                          Personalizzato
                        </span>
                      ) : (
                        <span data-testid="badge-weekly" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-lavender-200/70 dark:bg-lavender-900/40 text-lavender-900 dark:text-lavender-200">
                          <CalendarDays className="h-3 w-3" />
                          Automatico settimanale
                        </span>
                      )}
                    </div>
                    <div className="font-medium truncate" title={isCustom ? r.label : ""}>
                      {isCustom ? (r.label || "Report Personalizzato") : "Controllo camere"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {isCustom ? fmtRange(r.date_from, r.date_to) : fmtIsoWeek(r.week_iso, r.date_from, r.date_to)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => downloadPdf(r)}
                      data-testid={`download-pdf-${r.id}`}
                      title="Scarica PDF"
                      className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-lavender-100 dark:hover:bg-lavender-900/40 text-lavender-700 dark:text-lavender-300"
                    >
                      <FileDown className="h-4 w-4" />
                    </button>
                    {isSA && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`report-menu-${r.id}`}
                            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-accent text-muted-foreground"
                            title="Altre azioni"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[180px]">
                          <DropdownMenuItem
                            data-testid={`report-delete-${r.id}`}
                            onClick={() => setDeleteTarget(r)}
                            className="text-rose-600 focus:text-rose-600 gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Elimina report
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* KPI */}
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-display text-3xl tabular-nums">{kpiValue ?? "—"}</span>
                  <span className="text-xs text-muted-foreground">{kpiLabel}</span>
                  <div className="ml-auto"><DeltaBadge pct={cs.delta_pct} /></div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {cs.hotels_with_data ?? 0} hotel · generato il {new Date(r.generated_at).toLocaleDateString("it-IT")}
                </div>

                {/* Categorie (solo custom quality) */}
                {isCustom && !isProductivity && (r.categorie?.length ?? 0) > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1">
                    {(r.categorie || []).slice(0, 4).map((c) => (
                      <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-lavender-100/70 dark:bg-lavender-900/30 text-lavender-800 dark:text-lavender-200">
                        {labelForCat(c)}
                      </span>
                    ))}
                    {(r.categorie || []).length > 4 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">+{(r.categorie || []).length - 4}</span>
                    )}
                  </div>
                )}

                {cs.best_hotel && (
                  <div className="mt-3 pt-3 border-t border-border/40 flex items-center gap-2 text-xs">
                    <Trophy className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-muted-foreground">Migliore:</span>
                    <span className="font-medium truncate">{cs.best_hotel.hotel_name}</span>
                    <span className="tabular-nums text-emerald-600">
                      {isProductivity ? `${cs.best_hotel.produttivita_media} cam/h` : cs.best_hotel.punteggio}
                    </span>
                  </div>
                )}
                {cs.worst_hotel && cs.worst_hotel.hotel_id !== cs.best_hotel?.hotel_id && (
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                    <span className="text-muted-foreground">Attenzione:</span>
                    <span className="font-medium truncate">{cs.worst_hotel.hotel_name}</span>
                    <span className="tabular-nums text-rose-600">
                      {isProductivity ? `${cs.worst_hotel.produttivita_media} cam/h` : cs.worst_hotel.punteggio}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Custom builder modal */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="ih-glass-strong sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="custom-builder-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-lavender-500" />
              Nuovo report personalizzato
            </DialogTitle>
            <DialogDescription>
              Seleziona hotel, categorie di task e intervallo temporale. Range massimo 90 giorni.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Tipo di report */}
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Tipo di report</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="report-type-quality"
                  onClick={() => setCustomReportType("quality")}
                  className={`rounded-xl border-2 p-3 flex items-center gap-2.5 transition-colors ${customReportType === "quality" ? "border-lavender-500 bg-lavender-50/40 dark:bg-lavender-950/20" : "border-border/60 hover:border-lavender-300"}`}
                >
                  <span className="text-2xl">🎯</span>
                  <span className="flex-1 text-left">
                    <div className="text-sm font-medium">Controlli qualità</div>
                    <div className="text-[11px] text-muted-foreground">Punteggi + criticità</div>
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="report-type-productivity"
                  onClick={() => setCustomReportType("productivity")}
                  className={`rounded-xl border-2 p-3 flex items-center gap-2.5 transition-colors ${customReportType === "productivity" ? "border-sky-500 bg-sky-50/40 dark:bg-sky-950/20" : "border-border/60 hover:border-sky-300"}`}
                >
                  <span className="text-2xl">📈</span>
                  <span className="flex-1 text-left">
                    <div className="text-sm font-medium">Produttività HK</div>
                    <div className="text-[11px] text-muted-foreground">Cam/h + soglie</div>
                  </span>
                </button>
              </div>
            </div>

            {/* Titolo */}
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Titolo report</label>
              <Input
                data-testid="custom-title"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="Report Personalizzato · Ago-Set 2026"
                className="bg-background/40"
              />
            </div>

            {/* Hotel multi-select */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  Hotel <span className="text-rose-500 normal-case tracking-normal">*</span>
                </label>
                <button
                  type="button"
                  data-testid="custom-select-all-hotels"
                  onClick={toggleAllHotels}
                  className="text-xs text-lavender-700 dark:text-lavender-300 hover:underline"
                >
                  {selectedHotels.length === availableHotels.length ? "Deseleziona tutti" : "Seleziona tutti"}
                </button>
              </div>
              <div className="rounded-xl border border-border/60 bg-background/40 p-2 max-h-40 overflow-y-auto">
                {availableHotels.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-3">Nessun hotel accessibile</div>
                ) : availableHotels.map((h) => (
                  <label key={h.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-lavender-50/50 dark:hover:bg-lavender-950/20 cursor-pointer text-sm">
                    <Checkbox
                      data-testid={`custom-hotel-${h.id}`}
                      checked={selectedHotels.includes(h.id)}
                      onCheckedChange={() => toggleHotel(h.id)}
                    />
                    <span className="flex-1 truncate">{h.nome}</span>
                    <span className="text-xs text-muted-foreground">{h.citta || ""}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">{selectedHotels.length} su {availableHotels.length} selezionati</p>
            </div>

            {/* Categorie multi-select (solo Quality) */}
            {customReportType === "quality" && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  Categorie task <span className="text-muted-foreground/60 normal-case tracking-normal">(vuoto = tutte)</span>
                </label>
                <button
                  type="button"
                  data-testid="custom-select-all-cats"
                  onClick={toggleAllCats}
                  className="text-xs text-lavender-700 dark:text-lavender-300 hover:underline"
                >
                  {selectedCats.length === availableCats.length ? "Deseleziona tutte" : "Tutte"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableCats.map((c) => {
                  const on = selectedCats.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      data-testid={`custom-cat-${c}`}
                      onClick={() => toggleCat(c)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on
                        ? "bg-lavender-500 text-white border-lavender-500"
                        : "border-border hover:bg-accent"
                      }`}
                    >{labelForCat(c)}</button>
                  );
                })}
              </div>
            </div>
            )}

            {/* Range date */}
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">
                <CalendarIcon className="h-3.5 w-3.5" /> Intervallo
              </label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { k: "last_7", l: "Ultimi 7 giorni" },
                  { k: "last_30", l: "Ultimi 30 giorni" },
                  { k: "last_month", l: "Mese scorso" },
                  { k: "custom", l: "Custom" },
                ].map((p) => (
                  <button
                    key={p.k}
                    type="button"
                    data-testid={`custom-preset-${p.k}`}
                    onClick={() => applyPreset(p.k)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${rangePreset === p.k
                      ? "bg-lavender-500 text-white border-lavender-500"
                      : "border-border hover:bg-accent"
                    }`}
                  >{p.l}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  data-testid="custom-date-from"
                  type="date"
                  value={rangeFrom}
                  onChange={(e) => { setRangeFrom(e.target.value); setRangePreset("custom"); }}
                  className="bg-background/40 w-auto"
                />
                <span className="text-sm text-muted-foreground">—</span>
                <Input
                  data-testid="custom-date-to"
                  type="date"
                  value={rangeTo}
                  onChange={(e) => { setRangeTo(e.target.value); setRangePreset("custom"); }}
                  className="bg-background/40 w-auto"
                />
                <span className={`text-xs tabular-nums ${rangeDays > 90 ? "text-rose-600" : "text-muted-foreground"}`}>
                  {rangeDays > 0 ? `${rangeDays} gg` : "—"}
                </span>
              </div>
              {rangeDays > 90 && (
                <p className="text-xs text-rose-600">Range massimo consentito: 90 giorni</p>
              )}
              {rangeFrom && rangeTo && new Date(rangeFrom) > new Date(rangeTo) && (
                <p className="text-xs text-rose-600">La data iniziale deve essere ≤ data finale</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>Annulla</Button>
            <Button
              data-testid="custom-generate-btn"
              onClick={submitCustom}
              disabled={!canGenerate || creating}
              className="gap-1.5"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Genera report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="ih-glass-strong sm:max-w-md" data-testid="delete-report-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-rose-600">
              <Trash2 className="h-5 w-5" />
              Eliminare il report?
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.tipo === "custom"
                ? <>Report personalizzato <strong className="text-foreground">"{deleteTarget?.label}"</strong> — {deleteTarget && fmtRange(deleteTarget.date_from, deleteTarget.date_to)}.</>
                : <>Report settimanale <strong className="text-foreground">{deleteTarget?.week_iso}</strong>.</>
              }
              <br />
              Questa operazione è <strong>irreversibile</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button
              data-testid="confirm-delete-report"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
